import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import {
  ChatBubbleLeftRightIcon, XMarkIcon,
  PaperAirplaneIcon, MinusIcon,
  ChevronDownIcon, ChevronUpIcon,
  PhoneIcon, ArrowLeftIcon, PaperClipIcon,
} from '@heroicons/react/24/outline';
import { chatService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { Spinner } from './index';

/* ─── Predefined FAQs ─────────────────────────────────────────────────────── */
const FAQS = [
  {
    q: "My PC / laptop won't turn on — what do I do?",
    a: "Check the power cable and ensure the outlet is working. Try a hard reset: hold the power button for 10 seconds, then press once to restart. If the problem persists, raise a Hardware Support ticket and a technician will assist you shortly.",
  },
  {
    q: 'My internet / network connection is down.',
    a: 'First, restart your router/switch and wait 60 seconds. Check if other devices on the same network are affected. If the issue is widespread, submit a "Networking" ticket with your location. For critical outages, call the IT hotline directly.',
  },
  {
    q: 'How do I reset my Windows / system password?',
    a: 'Visit the IT Support Portal login page and click "Forgot Password" to receive a reset link. If you are locked out of Windows entirely, raise an "Account & Access" ticket and a technician will unlock your account remotely.',
  },
  {
    q: 'How do I request new software or a hardware upgrade?',
    a: 'Open a new ticket with category "Software Issues" (for software) or "Hardware Support" (for hardware). Describe what you need and the business justification. Your IT Manager will review and approve the request.',
  },
  {
    q: 'What are your IT support response time SLAs?',
    a: 'Critical (server/network down): 1 hour\nHigh (major hardware fault): 4 hours\nMedium (software issues): 1 business day\nLow (general IT queries): 2 business days.',
  },
  {
    q: 'How do I track and follow up on my IT ticket?',
    a: 'Log in to the portal and go to "My Requests" in the sidebar. You can see real-time status updates, add comments, and attach files directly on the ticket page.',
  },
  {
    q: 'My printer is not working — quick fixes?',
    a: '1) Check the printer is powered on and connected to the network.\n2) Remove and re-add the printer in Windows Settings → Printers.\n3) Restart the Print Spooler service (Services → Print Spooler → Restart).\nIf still stuck, raise a Hardware Support ticket.',
  },
  {
    q: 'How do I connect to the company VPN?',
    a: 'Install the VPN client provided by IT (GlobalProtect / Cisco AnyConnect). Use your corporate email credentials. If you have not received the setup guide, raise an "Account & Access" ticket and IT will send the instructions to your registered email.',
  },
];

/* ─── Message bubble ─────────────────────────────────────────────────────────── */
const Bubble = ({ msg, currentUserId }) => {
  const isOwn = String(msg.sender?._id || msg.sender) === String(currentUserId);
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
        isOwn ? 'bg-[#12206b] text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'
      }`}>
        {!isOwn && (
          <p className="text-[10px] font-semibold mb-0.5 opacity-60">
            {msg.sender?.firstName || 'Support'}
          </p>
        )}
        <p className="leading-relaxed whitespace-pre-wrap break-words">{msg.body}</p>
        <p className={`text-[10px] mt-0.5 text-right ${isOwn ? 'text-[#90e0f3]' : 'text-gray-400'}`}>
          {msg.createdAt ? formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true }) : ''}
        </p>
      </div>
    </div>
  );
};

const LiveChatWidget = () => {
  const { user, isAuthenticated } = useAuth();
  const { features, appName, supportPhone, whiteLabelEnabled } = useTenant();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [minimised, setMinimised] = useState(false);
  const [step, setStep] = useState('faq'); // 'faq' | 'chat'
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [helpfulMap, setHelpfulMap] = useState({}); // { [faqIndex]: 'yes' | 'no' }
  const [sessionId, setSessionId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [chatFiles, setChatFiles] = useState([]);
  const chatAttachRef = useRef(null);
  const bottomRef = useRef(null);
  const prevMsgCount = useRef(0);
  const prevStatus = useRef(null);

  // Only visible for customers & if feature enabled
  const isCustomer = user?.role === 'customer';
  const visible = isAuthenticated && isCustomer && features?.liveChat;

  // Resume existing open session on mount
  useEffect(() => {
    if (!visible) return;
    chatService.getSessions().then((r) => {
      const active = r.data.data?.find((s) => ['waiting', 'active'].includes(s.status));
      if (active) {
        setSessionId(active._id);
        setStep('chat');
      }
    }).catch(() => {});
  }, [visible]);

  const { data: session } = useQuery(
    ['chat-widget', sessionId],
    () => chatService.getSession(sessionId).then((r) => r.data.data),
    {
      enabled: !!sessionId,
      refetchInterval: open && !minimised
        ? (prevStatus.current === 'waiting' ? 2000 : 3000)
        : 10000,
      staleTime: 0,
    }
  );

  // Detect status transitions (waiting → active, active → closed)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session) return;
    const prev = prevStatus.current;
    const curr = session.status;
    if (prev === 'waiting' && curr === 'active') {
      // Agent just joined — scroll will handle it
    }
    prevStatus.current = curr;
  }, [session?.status]);

  // Track unread when minimised
  useEffect(() => {
    if (!session) return;
    const count = session.messages?.length || 0;
    if ((minimised || !open) && count > prevMsgCount.current) {
      setUnread((u) => u + (count - prevMsgCount.current));
    }
    prevMsgCount.current = count;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.messages?.length, minimised, open]);

  // Scroll to bottom
  useEffect(() => {
    if (open && !minimised) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [session?.messages?.length, open, minimised]);

  const handleOpen = () => {
    setOpen(true);
    setMinimised(false);
    setUnread(0);
  };

  const handleStartChat = async () => {
    setStarting(true);
    try {
      const { data } = await chatService.start({ subject: 'Support Request' });
      setSessionId(data.data._id);
      setStep('chat');
      qc.invalidateQueries('chat-sessions');
    } catch {
      toast.error('Could not start chat. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!input.trim() && chatFiles.length === 0) || !sessionId) return;
    setSending(true);
    try {
      await chatService.sendMessage(sessionId, input.trim(), chatFiles);
      setInput('');
      setChatFiles([]);
      qc.invalidateQueries(['chat-widget', sessionId]);
    } catch {
      toast.error('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (sessionId && session?.status !== 'closed') {
      await chatService.close(sessionId).catch(() => {});
      qc.invalidateQueries(['chat-widget', sessionId]);
    }
    setOpen(false);
    setSessionId(null);
    setStep('faq');
    setExpandedFaq(null);
    setHelpfulMap({});
    setUnread(0);
  };

  if (!visible) return null;

  const isClosed = session?.status === 'closed';

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Chat window */}
      {open && !minimised && (
        <div
          className="w-[420px] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          style={{ height: 580, background: '#fff' }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #12206b, #0d1a56)' }}
          >
            <div className="flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="w-5 h-5 text-white/80" />
              <span className="text-white font-semibold">{appName} Support</span>
              {session?.status === 'active' && (
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              )}
            </div>
            <div className="flex gap-1">
              <button onClick={() => setMinimised(true)} className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
                <MinusIcon className="w-4 h-4" />
              </button>
              <button onClick={handleClose} className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          {step === 'faq' ? (
            /* ── FAQ / Pre-chat screen ── */
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Welcome banner */}
              <div className="px-4 pt-4 pb-3 border-b border-gray-100">
                      <p className="font-semibold text-gray-800">👋 Hi, how can we help with IT?</p>
                      <p className="text-xs text-gray-400 mt-0.5">Hardware, networking, development & more — choose a question or chat live.</p>
              </div>

              {/* FAQ list */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                {FAQS.map((faq, i) => (
                  <div key={i} className="rounded-xl border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <span>{faq.q}</span>
                      {expandedFaq === i
                        ? <ChevronUpIcon className="w-4 h-4 text-[#00b4d8] flex-shrink-0 ml-2" />
                        : <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />}
                    </button>
                    {expandedFaq === i && (
                      <div className="px-3 pb-3 pt-1 text-xs text-gray-600 bg-[#e8f7fc]/50 leading-relaxed">
                        <p>{faq.a}</p>
                        {helpfulMap[i] === 'yes' ? (
                          <div className="mt-2.5 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                            <span className="text-emerald-500 text-base">🎉</span>
                            <span className="text-emerald-700 font-medium">Thanks! Glad that helped.</span>
                          </div>
                        ) : helpfulMap[i] === 'no' ? (
                          <div className="mt-2.5 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 space-y-1.5">
                            <p className="text-amber-700 font-medium">No worries! Let us help you directly.</p>
                            <button
                              onClick={handleStartChat}
                              disabled={starting}
                              className="btn-primary w-full text-xs py-1.5 flex items-center justify-center gap-1.5"
                            >
                              {starting ? <Spinner size="sm" /> : (<><ChatBubbleLeftRightIcon className="w-3.5 h-3.5" /> Chat with Support</>)}
                            </button>
                          </div>
                        ) : (
                          <div className="mt-2.5 flex items-center gap-2">
                            <span className="text-xs text-gray-400">Was this helpful?</span>
                            <button
                              onClick={() => setHelpfulMap((m) => ({ ...m, [i]: 'yes' }))}
                              className="text-xs px-2.5 py-1 rounded-lg border border-emerald-300 text-emerald-600 hover:bg-emerald-50 transition-colors font-medium"
                            >👍 Yes</button>
                            <button
                              onClick={() => setHelpfulMap((m) => ({ ...m, [i]: 'no' }))}
                              className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors font-medium"
                            >👎 No</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Still need help */}
              <div className="px-4 pt-3 pb-4 border-t border-gray-100 space-y-2">
                <p className="text-xs text-gray-500 font-medium text-center">Still need help?</p>
                <button
                  onClick={handleStartChat}
                  disabled={starting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {starting ? <Spinner size="sm" /> : (
                    <><ChatBubbleLeftRightIcon className="w-4 h-4" /> Chat with Support</>
                  )}
                </button>
                {supportPhone && (
                <a
                  href={`tel:${supportPhone.replace(/\s/g, '')}`}
                  className="flex items-center justify-center gap-2 w-full border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl py-2 text-sm font-medium transition-colors"
                >
                  <PhoneIcon className="w-4 h-4 text-[#00b4d8]" />
                  Call {supportPhone}
                </a>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Back to FAQ bar */}
              <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0">
                <button
                  onClick={() => setStep('faq')}
                  className="flex items-center gap-1.5 text-xs text-[#12206b] hover:text-[#0a1545] transition-colors"
                >
                  <ArrowLeftIcon className="w-3.5 h-3.5" />
                  Back to FAQ
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 py-3 min-h-0">

                {/* ── Connecting banner ── */}
                {session?.status === 'waiting' && (
                  <div className="flex flex-col items-center justify-center gap-3 py-6">
                    <div className="relative w-12 h-12">
                      <div className="absolute inset-0 rounded-full border-4 border-[#e8f7fc]" />
                      <div className="absolute inset-0 rounded-full border-4 border-[#12206b] border-t-transparent animate-spin" />
                      <ChatBubbleLeftRightIcon className="absolute inset-0 m-auto w-5 h-5 text-[#12206b]" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-700 animate-pulse">Connecting to a support specialist…</p>
                      <p className="text-xs text-gray-400 mt-0.5">Please wait, we'll be right with you.</p>
                    </div>
                    <div className="flex gap-1 mt-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00b4d8] animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00b4d8] animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00b4d8] animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}

                {/* ── Agent joined banner ── */}
                {session?.status === 'active' && (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
                    <span className="text-xs text-emerald-700 font-medium">
                      {session?.agent?.firstName
                        ? `${session.agent.firstName} has joined the chat`
                        : 'A support specialist has joined the chat'} — you're connected!
                    </span>
                  </div>
                )}

                {session?.messages?.map((msg, i) => (
                  <Bubble key={msg._id || i} msg={msg} currentUserId={user?._id} />
                ))}

                {/* ── Chat closed / solved banner ── */}
                {isClosed && (
                  <div className="mt-3 rounded-2xl overflow-hidden border border-emerald-200">
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 px-4 py-5 flex flex-col items-center text-center gap-2">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-2xl">✅</div>
                      <p className="font-semibold text-gray-800 text-sm">Your problem has been solved!</p>
                      <p className="text-xs text-gray-500">Thank you for contacting us. We hope we could help!</p>
                      <div className="flex gap-2 mt-1 w-full">
                        <button
                          onClick={() => { setSessionId(null); setStep('faq'); }}
                          className="flex-1 text-xs border border-[#90e0f3] text-[#12206b] hover:bg-[#e8f7fc] rounded-xl py-2 font-medium transition-colors"
                        >
                          Back to FAQ
                        </button>
                        <button
                          onClick={() => { setSessionId(null); handleStartChat(); }}
                          className="flex-1 text-xs btn-primary py-2"
                        >
                          New Chat
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>

              {/* Input */}
              {!isClosed && (
                <div className="px-3 pb-3 pt-2 border-t border-gray-100 flex-shrink-0">
                  {/* File chips */}
                  {chatFiles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {chatFiles.map((f, i) => (
                        <span key={i} className="flex items-center gap-1 bg-[#e8f7fc] border border-[#90e0f3] text-[#12206b] text-[11px] rounded-lg px-2 py-0.5">
                          <PaperClipIcon className="w-3 h-3 flex-shrink-0" />
                          <span className="max-w-[120px] truncate">{f.name}</span>
                          <button type="button" onClick={() => setChatFiles((p) => p.filter((_, j) => j !== i))} className="ml-0.5 text-[#00b4d8] hover:text-red-500">
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <form onSubmit={handleSend} className="flex gap-2">
                    {/* Hidden file input */}
                    <input
                      ref={chatAttachRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.txt,.doc,.docx,.zip"
                      className="hidden"
                      onChange={(e) => {
                        const newFiles = Array.from(e.target.files);
                        setChatFiles((p) => [...p, ...newFiles].slice(0, 5));
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => chatAttachRef.current?.click()}
                      disabled={session?.status === 'waiting'}
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border border-gray-200 text-gray-400 hover:text-[#12206b] hover:border-[#90e0f3] hover:bg-[#e8f7fc] transition-colors disabled:opacity-40"
                      title="Attach file"
                    >
                      <PaperClipIcon className="w-4 h-4" />
                    </button>
                    <input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={session?.status === 'waiting' ? 'Connecting — please wait…' : 'Type a message…'}
                      disabled={session?.status === 'waiting'}
                      className="input flex-1 text-sm py-2 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                    />
                    <button
                      type="submit"
                      disabled={sending || (!input.trim() && chatFiles.length === 0) || session?.status === 'waiting'}
                      className="btn-primary w-9 h-9 rounded-xl flex items-center justify-center p-0 flex-shrink-0 disabled:opacity-50"
                    >
                      {sending ? <Spinner size="xs" /> : <PaperAirplaneIcon className="w-4 h-4" />}
                    </button>
                  </form>
                </div>
              )}
            </>
          )}          {/* Powered by Tzar Help Desk */}
          {!whiteLabelEnabled && (
            <div className="flex items-center justify-center py-2 border-t border-gray-100 flex-shrink-0">
              <p className="text-[10px] text-gray-400 text-center">
                Powered by{' '}
                <a href="https://tzarlabs.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#00b4d8] hover:text-[#12206b]">Tzar Help Desk</a>
              </p>
            </div>
          )}        </div>
      )}

      {/* Minimised bar */}
      {open && minimised && (
        <button
          onClick={() => { setMinimised(false); setUnread(0); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-2xl shadow-lg text-white text-sm font-medium"
          style={{ background: 'linear-gradient(135deg, #12206b, #0d1a56)' }}
        >
          <ChatBubbleLeftRightIcon className="w-4 h-4" />
          Support Chat
          {unread > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      )}

      {/* Floating bubble */}
      {!open && (
        <button
          onClick={handleOpen}
          className="w-14 h-14 rounded-full shadow-2xl flex items-center justify-center relative transition-transform hover:scale-110"
          style={{ background: 'linear-gradient(135deg, #12206b, #0d1a56)' }}
          title="Chat with Support"
        >
          <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      )}
    </div>
  );
};

export default LiveChatWidget;
